import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Button } from './ui/button';
import { Wallet, Target, Sparkles, ArrowRight, X, Gift, Shield, Coins } from 'lucide-react';

const STORAGE_KEY = 'bet4peer_onboarded_v1';

export default function OnboardingModal() {
    const { t } = useTranslation();
    const { isConnected, connect } = useWeb3();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    useEffect(() => {
        const seen = localStorage.getItem(STORAGE_KEY);
        if (!seen && !isConnected) {
            const timer = setTimeout(() => setOpen(true), 1500);
            return () => clearTimeout(timer);
        }
    }, [isConnected]);

    const close = () => {
        localStorage.setItem(STORAGE_KEY, '1');
        setOpen(false);
    };

    const next = () => setStep(s => Math.min(s + 1, 2));
    const prev = () => setStep(s => Math.max(s - 1, 0));

    const handleConnect = () => {
        close();
        setTimeout(() => connect(), 200);
    };

    if (!open) return null;

    const slides = [
        {
            icon: Sparkles,
            iconBg: 'bg-[#FFD700]/15',
            iconColor: 'text-[#FFD700]',
            title: t('onboarding.s1Title'),
            subtitle: t('onboarding.s1Subtitle'),
            body: t('onboarding.s1Body'),
            highlights: [
                { icon: Shield, text: t('onboarding.s1Hl1') },
                { icon: Coins, text: t('onboarding.s1Hl2') },
                { icon: Target, text: t('onboarding.s1Hl3') },
            ],
        },
        {
            icon: Target,
            iconBg: 'bg-[#0066CC]/15',
            iconColor: 'text-[#0066CC]',
            title: t('onboarding.s2Title'),
            subtitle: t('onboarding.s2Subtitle'),
            body: t('onboarding.s2Body'),
            steps: [
                { n: '01', label: t('onboarding.s2Step1') },
                { n: '02', label: t('onboarding.s2Step2') },
                { n: '03', label: t('onboarding.s2Step3') },
                { n: '04', label: t('onboarding.s2Step4') },
            ],
        },
        {
            icon: Wallet,
            iconBg: 'bg-[#00C853]/15',
            iconColor: 'text-[#00C853]',
            title: t('onboarding.s3Title'),
            subtitle: t('onboarding.s3Subtitle'),
            body: t('onboarding.s3Body'),
            bonus: true,
        },
    ];

    const slide = slides[step];
    const Icon = slide.icon;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
            data-testid="onboarding-modal-overlay"
            onClick={close}
        >
            <div
                className="relative w-full sm:max-w-md mx-auto bg-[#001A4D] sm:rounded-3xl rounded-t-3xl border-t-2 sm:border-2 border-[#FFD700]/30 shadow-2xl overflow-hidden animate-slide-up"
                onClick={(e) => e.stopPropagation()}
                data-testid="onboarding-modal"
            >
                {/* Close button */}
                <button
                    onClick={close}
                    className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                    aria-label={t('common.close')}
                    data-testid="onboarding-close"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Progress dots */}
                <div className="flex items-center justify-center gap-2 pt-6 pb-2">
                    {[0, 1, 2].map(i => (
                        <button
                            key={i}
                            onClick={() => setStep(i)}
                            className={`h-1.5 rounded-full transition-all ${
                                i === step ? 'w-8 bg-[#FFD700]' : 'w-1.5 bg-white/30'
                            }`}
                            aria-label={`Slide ${i + 1}`}
                            data-testid={`onboarding-dot-${i}`}
                        />
                    ))}
                </div>

                {/* Content */}
                <div className="px-6 sm:px-8 pt-4 pb-2 text-center">
                    <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl mx-auto mb-4 ${slide.iconBg} flex items-center justify-center`}>
                        <Icon className={`w-8 h-8 sm:w-10 sm:h-10 ${slide.iconColor}`} />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 leading-tight">{slide.title}</h2>
                    <p className="text-[#FFD700] text-sm font-semibold uppercase tracking-wider mb-3">{slide.subtitle}</p>
                    <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-5">{slide.body}</p>

                    {/* Slide 1: highlights */}
                    {slide.highlights && (
                        <div className="space-y-2 text-left mb-2">
                            {slide.highlights.map((h, i) => {
                                const HIcon = h.icon;
                                return (
                                    <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5">
                                        <HIcon className="w-4 h-4 text-[#00C853] shrink-0" />
                                        <span className="text-white/90 text-sm">{h.text}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Slide 2: steps */}
                    {slide.steps && (
                        <div className="grid grid-cols-2 gap-2 text-left mb-2">
                            {slide.steps.map((s, i) => (
                                <div key={i} className="px-3 py-3 rounded-xl bg-white/5">
                                    <div className="text-[#FFD700] font-mono text-xs mb-1">{s.n}</div>
                                    <div className="text-white/90 text-xs leading-snug">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Slide 3: bonus */}
                    {slide.bonus && (
                        <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#FFD700]/15 to-[#FFA500]/10 border border-[#FFD700]/30 mb-2">
                            <Gift className="w-6 h-6 text-[#FFD700] shrink-0" />
                            <div className="text-left">
                                <div className="text-[#FFD700] font-bold text-sm">{t('onboarding.bonusTitle')}</div>
                                <div className="text-white/70 text-xs">{t('onboarding.bonusDesc')}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="px-6 sm:px-8 pb-6 sm:pb-7 pt-4 space-y-2">
                    {step < 2 ? (
                        <>
                            <Button
                                onClick={next}
                                className="w-full bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold h-12 text-base"
                                data-testid="onboarding-next"
                            >
                                {t('onboarding.next')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            <button
                                onClick={close}
                                className="w-full text-white/50 hover:text-white text-sm py-2 transition-colors"
                                data-testid="onboarding-skip"
                            >
                                {t('onboarding.skip')}
                            </button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={handleConnect}
                                className="w-full bg-[#00C853] text-white hover:bg-[#00B248] font-bold h-12 text-base"
                                data-testid="onboarding-connect"
                            >
                                <Wallet className="w-5 h-5 mr-2" />
                                {t('common.connectWallet')}
                            </Button>
                            <div className="flex gap-2">
                                <button
                                    onClick={prev}
                                    className="flex-1 text-white/50 hover:text-white text-sm py-2 transition-colors"
                                    data-testid="onboarding-back"
                                >
                                    {t('common.back')}
                                </button>
                                <a
                                    href="https://metamask.io/download/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 text-[#FFD700] hover:text-[#FFC700] text-sm py-2 transition-colors"
                                    data-testid="onboarding-no-wallet"
                                >
                                    {t('onboarding.noWallet')}
                                </a>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                .animate-fade-in { animation: fadeIn 0.25s ease-out; }
                .animate-slide-up { animation: slideUp 0.35s cubic-bezier(0.22, 1, 0.36, 1); }
            `}</style>
        </div>
    );
}
